// pages/Stores.js
import React, { useState, useEffect, useRef } from 'react';
import { 
  getStores, 
  createStore, 
  updateStore, 
  deactivateStore, 
  activateStore 
} from '../services/storeService';
import { getIcon } from '../utils/icons';
import { toast } from 'react-toastify';

const Stores = () => {
  const initialPage = parseInt(new URLSearchParams(window.location.search).get('page') || '1', 10);

  // Состояния
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingStore, setEditingStore] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    region: '',
    postalCode: '',
    phone: '',
    email: '',
    latitude: null,
    longitude: null
  });
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [page, setPage] = useState(initialPage);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [regionFilter, setRegionFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [regions, setRegions] = useState([]);
  const [cities, setCities] = useState([]);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [showInactive, setShowInactive] = useState(true); // По умолчанию показываем неактивные
  const [showMapView, setShowMapView] = useState(false);

  // Загрузка магазинов при монтировании компонента и изменении страницы
  useEffect(() => {
    fetchStores(page);
    const params = new URLSearchParams();
    if (page > 1) params.set('page', page.toString());
    const newUrl = window.location.pathname + (params.toString() ? `?${params.toString()}` : '');
    window.history.replaceState({}, '', newUrl);
  }, [page]);

  const filtersInitialized = useRef(false);

  // Обработка изменения поисковых фильтров с задержкой
  useEffect(() => {
    if (!filtersInitialized.current) {
      filtersInitialized.current = true;
      return;
    }

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    setSearchTimeout(setTimeout(() => {
      setPage(1);
      fetchStores(1);
    }, 500));

    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTerm, regionFilter, cityFilter, showInactive]);

  // Функция загрузки всех магазинов
  const fetchStores = async (pageNumber) => {
    try {
      setLoading(true);
      
      // Получаем данные с реального API
      const data = await getStores({}, showInactive);
      
      // Получение уникальных регионов и городов для фильтров
      const uniqueRegions = [...new Set(data
        .filter(s => s.region) // только непустые значения
        .map(s => s.region))];
      setRegions(uniqueRegions);
      
      const uniqueCities = [...new Set(data
        .filter(s => s.city) // только непустые значения
        .map(s => s.city))];
      setCities(uniqueCities);
      
      // Фильтрация по поисковому запросу, если есть
      let filteredStores = data;
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        filteredStores = filteredStores.filter(s => 
          (s.name && s.name.toLowerCase().includes(searchLower)) || 
          (s.address && s.address.toLowerCase().includes(searchLower)) || 
          (s.email && s.email.toLowerCase().includes(searchLower)) ||
          (s.phone && s.phone.includes(searchLower))
        );
      }
      
      // Фильтрация по региону, если выбран
      if (regionFilter) {
        filteredStores = filteredStores.filter(s => s.region === regionFilter);
      }
      
      // Фильтрация по городу, если выбран
      if (cityFilter) {
        filteredStores = filteredStores.filter(s => s.city === cityFilter);
      }
      
      setStores(filteredStores);
      
      // Расчет пагинации (предполагаем 10 элементов на страницу)
      const itemsPerPage = 10;
      setTotalPages(Math.max(1, Math.ceil(filteredStores.length / itemsPerPage)));
      setPage(pageNumber);
      
    } catch (error) {
      console.error('Ошибка загрузки магазинов:', error);
      toast.error('Не удалось загрузить магазины');
    } finally {
      setLoading(false);
    }
  };

  // Открытие модального окна для добавления/редактирования
  const handleOpenModal = (store = null) => {
    if (store) {
      setEditingStore(store);
      setFormData({
        name: store.name,
        address: store.address || '',
        city: store.city || '',
        region: store.region || '',
        postalCode: store.postalCode || '',
        phone: store.phone || '',
        email: store.email || '',
        latitude: store.latitude || null,
        longitude: store.longitude || null
      });
    } else {
      setEditingStore(null);
      setFormData({
        name: '',
        address: '',
        city: '',
        region: '',
        postalCode: '',
        phone: '',
        email: '',
        latitude: null,
        longitude: null
      });
    }
    setShowModal(true);
  };

  // Закрытие модального окна
  const handleCloseModal = () => {
    setShowModal(false);
    setEditingStore(null);
  };

  // Обработка изменения полей формы
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Специальная обработка для числовых полей
    if (name === 'latitude' || name === 'longitude') {
      setFormData((prev) => ({
        ...prev,
        [name]: value === '' ? null : parseFloat(value)
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Отправка формы (создание/обновление магазина)
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      let result;
      
      if (editingStore) {
        // Обновление существующего магазина
        result = await updateStore(editingStore.id, formData);
        toast.success('Магазин успешно обновлен');
      } else {
        // Создание нового магазина
        result = await createStore(formData);
        toast.success('Магазин успешно создан');
      }
      
      // Перезагружаем список магазинов
      fetchStores(page);
      handleCloseModal();
    } catch (error) {
      console.error('Ошибка сохранения магазина:', error);
      toast.error(error.response?.data?.message || 'Не удалось сохранить магазин');
    }
  };

  // Открытие диалога подтверждения удаления
  const openDeleteConfirm = (id) => {
    setConfirmDelete(id);
  };

  // Закрытие диалога подтверждения удаления
  const closeDeleteConfirm = () => {
    setConfirmDelete(null);
  };

  // Обработка деактивации магазина
  const handleDeactivate = async (id) => {
    try {
      // Используем специальный метод DELETE для деактивации
      await deactivateStore(id);
      fetchStores(page);
      toast.success('Магазин успешно деактивирован');
      if (confirmDelete) closeDeleteConfirm();
    } catch (error) {
      console.error('Ошибка деактивации магазина:', error);
      toast.error('Не удалось деактивировать магазин');
    }
  };
  
  // Обработка активации магазина
  const handleActivate = async (id) => {
    try {
      // Находим магазин в списке
      const store = stores.find(s => s.id === id);
      if (!store) {
        toast.error('Магазин не найден');
        return;
      }
      
      // Активируем магазин, передавая все его текущие данные
      await activateStore(id, {
        name: store.name,
        address: store.address || '',
        city: store.city || '',
        region: store.region || '',
        postalCode: store.postalCode || '',
        phone: store.phone || '',
        email: store.email || '',
        latitude: store.latitude || null,
        longitude: store.longitude || null
      });
      
      fetchStores(page);
      toast.success('Магазин успешно активирован');
    } catch (error) {
      console.error('Ошибка активации магазина:', error);
      toast.error('Не удалось активировать магазин');
    }
  };

  // Обработка переключения между видом списка и карты
  const toggleMapView = () => {
    setShowMapView(!showMapView);
  };

  return (
    <div>
      <header className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Магазины</h1>
          <p className="text-gray-600">Управление магазинами системы</p>
        </div>
        
        <div className="mt-4 md:mt-0 flex flex-col md:flex-row gap-2">
          <button
            onClick={toggleMapView}
            className={`px-4 py-2 rounded-md ${
              showMapView 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {showMapView ? 'Список' : 'Карта'}
          </button>
          
          <button
            onClick={() => handleOpenModal()}
            className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md flex items-center"
          >
            {getIcon('plus')}
            <span className="ml-2">Добавить магазин</span>
          </button>
        </div>
      </header>
      
      <div className="bg-white rounded-lg shadow-sm mb-6">
        <div className="p-4 border-b border-gray-200">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex-1">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  {getIcon('search')}
                </div>
                <input
                  type="text"
                  placeholder="Поиск по названию, адресу, телефону..."
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            
            <div className="flex-1 md:max-w-xs">
              <select
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={regionFilter}
                onChange={(e) => setRegionFilter(e.target.value)}
              >
                <option value="">Все регионы</option>
                {regions.map((region, index) => (
                  <option key={index} value={region}>{region}</option>
                ))}
              </select>
            </div>
            
            <div className="flex-1 md:max-w-xs">
              <select
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={cityFilter}
                onChange={(e) => setCityFilter(e.target.value)}
              >
                <option value="">Все города</option>
                {cities.map((city, index) => (
                  <option key={index} value={city}>{city}</option>
                ))}
              </select>
            </div>
            
            <div className="flex items-center">
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                />
                <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                <span className="ms-3 text-sm font-medium text-gray-900 dark:text-gray-300">Показывать неактивные</span>
              </label>
            </div>
          </div>
        </div>
        
        {showMapView ? (
          <div className="p-4">
            <div className="border border-gray-300 rounded-lg overflow-hidden" style={{ height: '500px' }}>
              <div className="flex items-center justify-center h-full bg-gray-100">
                <div className="text-center p-6">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                  </svg>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Интеграция карты</h3>
                  <p className="text-gray-600">
                    В рабочей версии здесь будет интерактивная карта с отмеченными магазинами - OpenStreetMap <br/>
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                </div>
              ) : (
                <table className="min-w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Название
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Адрес
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Город
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Регион
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Телефон
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Статус
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Действия
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {stores.length > 0 ? (
                      stores.map((store) => (
                        <tr key={store.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-gray-900">{store.name}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">{store.address || '-'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">{store.city || '-'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">{store.region || '-'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">{store.phone || '-'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                store.isActive
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-red-100 text-red-800'
                              }`}
                            >
                              {store.isActive ? 'Активен' : 'Неактивен'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleOpenModal(store)}
                                className="text-blue-600 hover:text-blue-900"
                                title="Редактировать"
                              >
                                {getIcon('edit')}
                              </button>
                              {store.isActive ? (
                                <button
                                  onClick={() => openDeleteConfirm(store.id)}
                                  className="text-red-600 hover:text-red-900"
                                  title="Деактивировать"
                                >
                                  {getIcon('block')}
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleActivate(store.id)}
                                  className="text-green-600 hover:text-green-900"
                                  title="Активировать"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className="px-6 py-4 text-center text-sm font-medium text-gray-500">
                          Магазины не найдены. Добавьте первый магазин!
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
            
            <div className="p-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  Страница {page} из {totalPages}
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => page > 1 && setPage(page - 1)}
                    disabled={page === 1}
                    className={`px-3 py-1 rounded-md ${page === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:bg-blue-100'}`}
                  >
                    Назад
                  </button>
                  <button
                    onClick={() => page < totalPages && setPage(page + 1)}
                    disabled={page === totalPages}
                    className={`px-3 py-1 rounded-md ${page === totalPages ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:bg-blue-100'}`}
                  >
                    Далее
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Модальное окно для добавления/редактирования магазина */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full">
            <h2 className="text-xl font-bold mb-4">
              {editingStore ? 'Редактирование магазина' : 'Добавление магазина'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="name">
                  Название *
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="Введите название магазина"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="border border-gray-300 rounded-md w-full py-2 px-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="address">
                    Адрес *
                  </label>
                  <input
                    id="address"
                    name="address"
                    type="text"
                    placeholder="Введите адрес"
                    value={formData.address}
                    onChange={handleChange}
                    required
                    className="border border-gray-300 rounded-md w-full py-2 px-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="city">
                    Город *
                  </label>
                  <input
                    id="city"
                    name="city"
                    type="text"
                    placeholder="Введите город"
                    value={formData.city}
                    onChange={handleChange}
                    required
                    className="border border-gray-300 rounded-md w-full py-2 px-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="region">
                    Регион
                  </label>
                  <input
                    id="region"
                    name="region"
                    type="text"
                    placeholder="Введите регион"
                    value={formData.region}
                    onChange={handleChange}
                    className="border border-gray-300 rounded-md w-full py-2 px-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="postalCode">
                    Почтовый индекс
                  </label>
                  <input
                    id="postalCode"
                    name="postalCode"
                    type="text"
                    placeholder="Введите почтовый индекс"
                    value={formData.postalCode}
                    onChange={handleChange}
                    className="border border-gray-300 rounded-md w-full py-2 px-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="phone">
                    Телефон
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    type="text"
                    placeholder="Введите телефон"
                    value={formData.phone}
                    onChange={handleChange}
                    className="border border-gray-300 rounded-md w-full py-2 px-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="email">
                    Email
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="Введите email"
                    value={formData.email}
                    onChange={handleChange}
                    className="border border-gray-300 rounded-md w-full py-2 px-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="latitude">
                    Широта
                  </label>
                  <input
                    id="latitude"
                    name="latitude"
                    type="number"
                    step="0.000001"
                    placeholder="Введите широту"
                    value={formData.latitude === null ? '' : formData.latitude}
                    onChange={handleChange}
                    className="border border-gray-300 rounded-md w-full py-2 px-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="longitude">
                    Долгота
                  </label>
                  <input
                    id="longitude"
                    name="longitude"
                    type="number"
                    step="0.000001"
                    placeholder="Введите долготу"
                    value={formData.longitude === null ? '' : formData.longitude}
                    onChange={handleChange}
                    className="border border-gray-300 rounded-md w-full py-2 px-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <div className="flex justify-end pt-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded mr-2"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                >
                  Сохранить
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Диалог подтверждения удаления */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full">
            <h3 className="text-lg font-bold mb-4">Подтверждение деактивации</h3>
            <p className="mb-6">Вы уверены, что хотите деактивировать этот магазин? Это может повлиять на связанные данные.</p>
            <div className="flex justify-end">
              <button
                onClick={closeDeleteConfirm}
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded mr-2"
              >
                Отмена
              </button>
              <button
                onClick={() => handleDeactivate(confirmDelete)}
                className="bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded"
              >
                Деактивировать
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Stores;